import { DataTestId } from 'core/common/constants';
import { useConfig } from 'core/common/hooks';
import { useOffers } from 'core/offers/hooks';
import {
  PaymentFormParams,
  PaymentFormType,
  PaymentInteraction,
  PaymentOrderDescription,
  PaymentType,
} from 'core/payments/entities';
import PaymentCard, { ApplePayWrapper, GooglePayWrapper } from '../PaymentCard';
import { usePaymentForm } from '../PaymentForm/usePaymentForm';
import { ContainerStyled, HiddenStyled } from './styled';

const params: PaymentFormParams = {
  applePayButtonParams: { enabled: true, type: 'order' },
  googlePayButtonParams: { enabled: true, type: 'order' },
};

type ExpressPayButtonProps = {
  paymentFormType: PaymentFormType;
  onClick?: () => void;
  onPaymentSuccess?: (data: { order?: PaymentOrderDescription }) => void;
  onPaymentFailed?: () => void;
};

export const ExpressPayButton = ({
  paymentFormType,
  onClick,
  onPaymentSuccess,
  onPaymentFailed,
}: ExpressPayButtonProps) => {
  const config = useConfig();
  const { selectedOffer } = useOffers();

  const {
    shouldRenderApplePay,
    shouldRenderGooglePay,
    paymentFormMerchantData,
    paymentFormFailed,
    cardFormError,
    paymentVerify,
    paymentSucceed,
    paymentClick,
    paymentMethodClose,
  } = usePaymentForm({
    productId: selectedOffer?.offerId ?? '',
    currency: selectedOffer?.currency,
    paymentFormType,
    onPaymentSuccess,
    onPaymentFailed,
  });

  const isApplePay = shouldRenderApplePay;
  const isGooglePay = !isApplePay && shouldRenderGooglePay;

  if (!selectedOffer || (!isApplePay && !isGooglePay)) {
    return null;
  }

  const walletType = isApplePay ? PaymentType.APPLE_PAY : PaymentType.GOOGLE_PAY;

  const handleWalletInteraction = (data: PaymentInteraction) => {
    if (data.target.interaction === 'click') {
      if (onClick) {
        onClick();
      }

      paymentClick(walletType);
    }

    if (data.target.interaction === 'pageClose') {
      paymentMethodClose(walletType);
    }
  };

  const handleSucceed = (data: { order?: PaymentOrderDescription }) => {
    paymentSucceed(data, walletType);
  };

  return (
    <ContainerStyled>
      {isApplePay ? (
        <ApplePayWrapper
          id={config.applePayContainerId}
          data-testid={DataTestId.APPLE_PAY_BUTTON}
        />
      ) : (
        <GooglePayWrapper
          id={config.googlePayContainerId}
          data-testid={DataTestId.GOOGLE_PAY_BUTTON}
        />
      )}
      <HiddenStyled>
        <PaymentCard
          formMerchantData={paymentFormMerchantData}
          params={params}
          shouldRenderGooglePay
          onFailed={paymentFormFailed}
          onError={cardFormError}
          onVerify={paymentVerify}
          onSucceed={handleSucceed}
          onInteraction={handleWalletInteraction}
        />
        {isApplePay ? (
          <div id={config.googlePayContainerId} />
        ) : (
          <div id={config.applePayContainerId} />
        )}
      </HiddenStyled>
    </ContainerStyled>
  );
};
